// Tab selection that survives a refresh, a shared link and the back button.
//
// WHY THIS IS A HOOK AND NOT `useState('overview')` IN EACH PAGE.
//
// Every tabbed detail page had written its own `const [tab, setTab] = useState(...)`, and
// plain component state has three faults an operator hits daily:
//
//   1. REFRESH LOSES THE TAB. Kevin, 2026-08-13: "when you refresh the page and you are not
//      on the main tab, you are sent back to the main tab." The line page's Routing tab, the
//      device page's GDMS tab, the DID page's tabs — all of them reset to the first tab on
//      F5, which on a page you refresh to see whether a write landed is exactly the wrong
//      moment to lose your place.
//   2. BACK LEAVES THE PAGE. A tab click that writes no history entry means the browser's
//      back button skips past every tab you opened and exits the record entirely. Verified
//      live: back from the device page's GDMS tab landed on /devices, not on the device's
//      own Overview.
//   3. THE TAB IS NOT LINKABLE. "Look at the GDMS tab on this handset" cannot be sent as a
//      URL.
//
// SubscriberDetail already solved this for itself with a hash and got it right; the detail
// pages below it did not. So the working version moves here and they all import it, which
// is the only way the next tabbed page gets it for free.
//
// REPLACE, NOT PUSH. Selecting a tab uses `replaceState`, so opening four tabs does not put
// four entries in the history stack and does not make the back button a tab-rewinder. What
// back does is leave the page — and it leaves it to the URL you arrived from, tab and all,
// because that entry was written with the hash in place. That is the behaviour the
// subscriber page already had and the one operators are used to.

import { useState, useEffect, useCallback } from 'react';

/** The tab id in the URL right now, or null when there is no hash. */
function hashTab() {
  if (typeof window === 'undefined') return null;
  return window.location.hash.replace('#', '') || null;
}

/**
 * `?tab=users` as well as `#users`.
 *
 * The hash is the canonical form and always has been, but `?tab=` is what people
 * type — and it did not merely fail, it failed SILENTLY: the page opened on the
 * default tab with the URL still reading `?tab=users`, so the address bar and the
 * screen disagreed and the link looked like it had worked. That is the same
 * "reads as a broken page rather than a bad link" failure the `valid` option
 * exists to prevent, arriving through a different door.
 *
 * The hash wins when both are present — it is the form this app writes itself.
 */
function queryTab() {
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get('tab') || null;
  } catch {
    return null;
  }
}

/** Drop `?tab=` once it has been honoured, leaving every other param alone. */
function stripTabParam() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('tab')) return;
  url.searchParams.delete('tab');
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}

/**
 * @param defaultTab  the tab shown when the URL names none
 * @param options.aliases  old tab ids mapped to current ones, so a bookmark or a link
 *                         written before a page was reorganised still lands somewhere
 *                         sensible rather than silently falling back to the default
 * @param options.valid    the ids this page actually has. An unknown id — a stale link, a
 *                         typo — falls back to the default instead of rendering a page with
 *                         every panel hidden, which reads as a broken page rather than a bad
 *                         link.
 * @returns [activeTab, selectTab]
 */
export default function useHashTab(defaultTab, { aliases = {}, valid = null } = {}) {
  const canonical = useCallback((id) => {
    const mapped = aliases[id] || id || defaultTab;
    if (valid && !valid.includes(mapped)) return defaultTab;
    return mapped;
  }, [aliases, valid, defaultTab]);

  const [activeTab, setActiveTab] = useState(() => canonical(hashTab() ?? queryTab()));

  // A `?tab=` link is honoured once and then rewritten to the canonical `#tab`
  // form, so the URL an operator copies from the address bar afterwards is the one
  // this app understands everywhere — and so back/forward, which only listen to the
  // hash, behave the same however the page was reached.
  useEffect(() => {
    if (!hashTab() && queryTab()) {
      window.history.replaceState(null, '', `#${activeTab}`);
      stripTabParam();
    }
    // Deliberately once, on mount: this is about the URL the page was OPENED with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectTab = useCallback((id) => {
    const tab = canonical(id);
    setActiveTab(tab);
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', `#${tab}`);
    }
  }, [canonical]);

  // The URL can change without this component remounting: the browser's back and forward
  // buttons within one page, and any other code that edits the hash. Without this listener
  // the address bar and the rendered tab disagree, which is worse than either being wrong on
  // its own — the operator reads the URL as the truth and it is lying.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onPop = () => setActiveTab(canonical(hashTab()));
    window.addEventListener('popstate', onPop);
    window.addEventListener('hashchange', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('hashchange', onPop);
    };
  }, [canonical]);

  return [activeTab, selectTab];
}
